import React, { useState, useContext, useEffect } from "react";
import { Box, Button, TextField, Typography, IconButton, Grid } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { ThemeContext } from "../../context/ThemeContext";
import { modifyPositionSLTP } from '../../api/request/dashboard/dashboard'
import { useSelector, shallowEqual, useDispatch } from "react-redux";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import BidValue from "../global/BidAsk/BidValue";
import AskValue from "../global/BidAsk/AskValue";
import { deletePositionsData } from "../../store/Slices/orderPositionSlice";
import { closeOrderPosition } from "../../api/request/dashboard/dashboard";


const ModifyPosition = ({ onClose, onOrderModified }) => {
    const { modifyPositionDetail = {} } = useSelector((state) => state.globalSymbol, shallowEqual)
    const modifyPositionDetailForModify = modifyPositionDetail?.row || modifyPositionDetail;
    const orderSLTP = useSelector((state) => state.orderPosition.orderSLTP, shallowEqual);
    const matchingSLTP = orderSLTP.find((sltp) => sltp.positionId === modifyPositionDetailForModify.id);
    const dispatch = useDispatch()
    const [volume, setVolume] = useState(modifyPositionDetailForModify?.amount);
    const [stopLoss, setStopLoss] = useState(matchingSLTP?.stopLoss || null);
    const [takeProfit, setTakeProfit] = useState(matchingSLTP?.takeProfit || null);
    const { darkMode } = useContext(ThemeContext);
    const { accountId } = useSelector(state => state.accountSummary)
    const [isSuccess, setIsSuccess] = useState(false);
    const { symbols } = useSelector(state => state.watchlist)
    const symbolId = modifyPositionDetailForModify?.symbolId
    const symbolInfo = symbols[symbolId];
    const allSymbolParams = useSelector((state) => state.symbol.allSymbolParams);
    const symbolParameterData = allSymbolParams?.[symbolId] || null;
    const { t } = useTranslation()
    const [tpValidation, setTpValidation] = useState(0)
    const [slValidation, setSlValidation] = useState(0)
    const symbol = useSelector(state => state.updateSymbol.symbols?.[symbolId])
    const stopOffset = (symbolParameterData?.stopOffset) / Math.pow(10, symbolInfo?.digits);
    const limitOffset = (symbolParameterData?.limitOffset ?? 10) * (1 / Math.pow(10, symbolInfo?.digits));
    const decimalPlaces = useSelector((state) => state.user.decimalPlacesForAmountFormatting);

    const price = modifyPositionDetailForModify?.side === 1 ? symbol?.bid : symbol?.ask;
    const handleVolumeChange = (change) => {
        setVolume((prevVolume) => {
            if (!symbolParameterData) return prevVolume;

            const { minimumAmountPerDeal } = symbolParameterData;
            const maximumAmount = modifyPositionDetailForModify?.amount ?? symbolParameterData.maximumAmountPerDeal;

            let newVolume = prevVolume + change;
            newVolume = Math.round(newVolume / minimumAmountPerDeal) * minimumAmountPerDeal;

            if (newVolume < minimumAmountPerDeal) newVolume = minimumAmountPerDeal;
            if (newVolume > maximumAmount) newVolume = maximumAmount;

            return newVolume;
        });
    };

    const handleChangeVolume = (value) => {
        const maximumAmount = modifyPositionDetailForModify?.amount ?? symbolParameterData?.maximumAmountPerDeal;
        const numericValue = parseFloat(value);

        if (isNaN(numericValue)) return;

        const decimals = value.split('.')?.[1];
        if (!decimals || decimals.length <= decimalPlaces) {
            if (numericValue >= symbolParameterData.minimumAmountPerDeal && numericValue <= maximumAmount) {
                setVolume(numericValue);
            }
        }
    };



    const calculateStopLoss = () => {
        if (price === null) return null;

        return modifyPositionDetailForModify?.side === 1
            ? (Number(price) - limitOffset).toFixed(symbolInfo.digits) // Buy Condition
            : (Number(price) + stopOffset).toFixed(symbolInfo.digits); // Sell Condition
    };

    const calculateTakeProfit = () => {
        if (price === null) return null;

        return modifyPositionDetailForModify?.side === 1
            ? (Number(price) + stopOffset).toFixed(symbolInfo.digits) // Buy Condition
            : (Number(price) - limitOffset).toFixed(symbolInfo.digits); // Sell Condition
    };

    useEffect(() => {
        if (price) {
            setSlValidation(calculateStopLoss())
            setTpValidation(calculateTakeProfit())
        }

    }, [price])

    const handleClose = () => {
        onClose();
    };

    const handleModifyClick = async () => {

        try {
            const updatedOrder = {
                positionId: modifyPositionDetailForModify?.id,
                accountId: accountId,
                amount: volume,
                stopLoss: parseFloat(stopLoss),
                takeProfit: parseFloat(takeProfit),
                // comment,
            };
            console.log('called', updatedOrder);
            await modifyPositionSLTP(updatedOrder);
            toast.success(`Successfully manage the position #${modifyPositionDetailForModify?.id}`)

            if (onOrderModified) {
                onOrderModified();  // Call the function here after modification
            }
            setIsSuccess(true);
            handleClose();
        } catch (error) {
            const errorMessage = error?.response?.data?.message || "An unknown error occurred";

            toast.error(`${errorMessage}`);
            console.error("Error modifying order:", errorMessage, error);
        }

    };

    const handleChangeTP = (isIncrease) => {
        setTakeProfit((prev) => {
            let currentValue = parseFloat(prev);
            if (isNaN(currentValue) || currentValue === 0) {
                return tpValidation;
            }
            const step = 1 / Math.pow(10, symbolInfo?.digits || 2);
            const newValue = isIncrease ? currentValue + step : currentValue - step;
            return Math.max(0, newValue).toFixed(symbolInfo?.digits);
        });
    };
    const handleSLChange = (isIncrease) => {
        setStopLoss((prev) => {
            let currentValue = parseFloat(prev);
            if (isNaN(currentValue) || currentValue === 0) {
                return slValidation;
            }
            const step = 1 / Math.pow(10, symbolInfo?.digits || 2);
            const newValue = isIncrease ? currentValue + step : currentValue - step;
            return Math.max(0, newValue).toFixed(symbolInfo?.digits);
        });
    };

    const handleChangeSL = (value) => {
        const desimals = value.split('.')?.[1]
        if (!desimals) {
            setStopLoss(value);
            return;
        }
        if (desimals.length <= symbolInfo.digits) {
            setStopLoss(value)
        }
    }

    const handleTPChange = (value) => {
        const desimals = value.split('.')?.[1]
        if (!desimals) {
            setTakeProfit(value);
            return;
        }
        if (desimals.length <= symbolInfo.digits) {
            setTakeProfit(value)
        }
    }

    const handleCloseClick = async () => {
        if (!modifyPositionDetailForModify?.id) {
            toast.error("No order selected.");
            return;
        }

        try {
            const selectedRow = modifyPositionDetailForModify;
            const closeOrderPayload = {
                accountId: Number(accountId),
                positionId: selectedRow.id,
                amount: selectedRow.amount,
            };

            const response = await closeOrderPosition(closeOrderPayload);

            if (response.status === 200) {
                dispatch(deletePositionsData(selectedRow.id));

                const { symbolId, symbolName, side } = selectedRow;
                const price = side === 1 ? symbols[symbolId]?.bid : symbols[symbolId]?.ask;
                const tradeSide = side === 1 ? "Buy" : "Sell";

                toast.success(`Closed Trade #${selectedRow.id} (${symbolName}) at ${price}, ${tradeSide}`);
                onClose(); // Close the modal after successful order closure
            } else {
                toast.error(`Failed to close Trade #${selectedRow.id}`);
            }
        } catch (error) {
            console.error("Error closing order:", error);
            toast.error("❌ Error closing order. Please try again.");
        }
    };

    const handleClickClose = () => {
        handleCloseClick();
        onClose();
    }
    const side = modifyPositionDetailForModify?.side;
    const isDisabled = () => {
        const isSLFilled = stopLoss !== '' && stopLoss !== null && stopLoss !== undefined;
        const isTPFilled = takeProfit !== '' && takeProfit !== null && takeProfit !== undefined;

        if (!isSLFilled && !isTPFilled) {
            return true;
        }

        if (isSLFilled) {
            if (side === 1 && slValidation < stopLoss) return true;
            if (side === -1 && slValidation > stopLoss) return true;
        }

        if (isTPFilled) {
            if (side === 1 && tpValidation > takeProfit) return true;
            if (side === -1 && tpValidation < takeProfit) return true;
        }

        return false;
    };


    const handleSLBlur = (e) => {
        const value = parseFloat(e.target.value);
        if (!value || isNaN(value)) return;

        const minSL = parseFloat(slValidation);

        if (side === -1) {
            // Sell: SL should be less than or equal to current price
            if (minSL >= value) {
                setStopLoss(minSL.toFixed(symbolInfo.digits));
            } else {
                setStopLoss(value.toFixed(symbolInfo.digits));
            }
        } else if (side === 1) {
            // Buy: SL should be greater than or equal to current price
            if (minSL <= value) {
                setStopLoss(minSL.toFixed(symbolInfo.digits));
            } else {
                setStopLoss(value.toFixed(symbolInfo.digits));
            }
        }
    };

    const handleTPBlur = (e) => {
        const value = parseFloat(e.target.value);
        if (!value || isNaN(value)) return;

        const minTP = parseFloat(tpValidation);

        if (side === -1) {
            // Sell: TP should be less than or equal to current price
            if (minTP <= value) {
                setTakeProfit(minTP.toFixed(symbolInfo.digits));
            } else {
                setTakeProfit(value.toFixed(symbolInfo.digits));
            }
        } else if (side === 1) {
            // Buy: TP should be greater than or equal to current price
            if (minTP >= value) {
                setTakeProfit(minTP.toFixed(symbolInfo.digits));
            } else {
                setTakeProfit(value.toFixed(symbolInfo.digits));
            }
        }
    };


    return (
        <Box sx={{ height: '100%', width: '100%' }}>
            <Grid container spacing={1} sx={{ overflow: 'auto', height: '100%' }}>
                <Grid item xs={12} display={'flex'} justifyContent={'center'}>
                    <Box sx={{ width: "100%", bgcolor: darkMode ? "var(--background-color)" : 'var(--white)', fontSize: "0.7rem" }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1, border: `1px solid ${darkMode ? "var(--borderDark)" : 'var(--borderLight)'}`, height: 45 }} >
                            <Typography className="text-primary" sx={{ fontSize: '0.9rem', p: 1 }}>#{modifyPositionDetailForModify?.id}/{modifyPositionDetailForModify?.side === 1 ? "Buy" : 'Sell'} /{modifyPositionDetailForModify?.symbolName} / at price {modifyPositionDetailForModify?.openPrice} </Typography>
                            <IconButton onClick={handleClose} style={{ borderLeft: `1px solid ${darkMode ? "var(--borderDark)" : 'var(--borderLight)'}`, borderRadius: 0, color: '#3687ed', height: 45, width: 45 }} > <CloseIcon sx={{ fontSize: '1.3rem' }} /> </IconButton>
                        </Box>

                        <Box sx={{ p: 1, }}>
                            {isSuccess ? (
                                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", gap: 2, }}>
                                    <Typography sx={{ fontSize: "1rem", color: darkMode ? 'var(--lightText)' : "var(--black)" }}>{t('positionModificationSuccess')}</Typography>
                                    <Button variant="contained" onClick={handleClose} sx={{ fontSize: "0.9rem", height: 40 }}>{t('close')}</Button>
                                </Box>
                            ) : (
                                <>
                                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                                        <Typography sx={{ fontSize: '0.75rem', color: darkMode ? 'var(--lightText)' : "var(--black)", }}>{t('amount')}-({t('lot')})</Typography>
                                        <Typography variant="caption" align="right" sx={{ fontSize: '0.7rem', color: darkMode ? 'var(--lightText)' : "var(--black)", }}>{`${t('min')}: ${(symbolParameterData?.minimumAmountPerDeal).toFixed(decimalPlaces)}, ${t('max')}: ${(modifyPositionDetailForModify?.amount).toFixed(decimalPlaces)}`}</Typography>

                                    </Box>
                                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2, gap: 2, width: "100%" }}>
                                        <Box sx={{ width: "100%" }}>
                                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", border: `1px solid ${darkMode ? "var(--borderDark)" : 'var(--borderLight)'}`, overflow: "hidden" }}>
                                                <IconButton onClick={() => handleVolumeChange(-symbolParameterData?.minimumAmountPerDeal)} disabled={!symbolParameterData || volume <= symbolParameterData?.minimumAmountPerDeal} sx={{ height: 40, borderRight: `1px solid ${darkMode ? "var(--borderDark)" : 'var(--borderLight)'}`, borderRadius: 0, border: "none", "&.Mui-disabled": { opacity: 1, backgroundColor: darkMode ? "#3a3f45" : "#e0e0e0" } }}>
                                                    <RemoveIcon fontSize="small" sx={{ fontSize: '1rem', color: darkMode ? 'var(--lightText)' : "var(--black)", }} />
                                                </IconButton>
                                                <TextField  value={volume ? volume.toFixed(decimalPlaces) : '0.00'} size="small" inputProps={{ style: { textAlign: "center", fontSize: '0.9rem' } }}
                                                    onChange={(e) => handleChangeVolume(e.target.value)} type="number" fullWidth sx={{ height: 40, textAlign: "center", "& .MuiOutlinedInput-notchedOutline": { border: "none" }, color: darkMode ? 'var(--lightText)' : "var(--black)", }}
                                                />
                                                <IconButton onClick={() => handleVolumeChange(symbolParameterData?.minimumAmountPerDeal)}  disabled={!symbolParameterData || volume >= modifyPositionDetailForModify?.amount} sx={{ height: 40, border: 'none', borderRadius: 0, "&.Mui-disabled": { opacity: 1, backgroundColor: darkMode ? "#3a3f45" : "#e0e0e0" } }}>
                                                    <AddIcon fontSize="small" sx={{ fontSize: '1rem', color: darkMode ? 'var(--lightText)' : "var(--black)", }} />
                                                </IconButton>
                                            </Box>
                                        </Box>


                                    </Box>
                                    {/* Stop Loss and Take Profit Section */}
                                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2, gap: 2 }}>
                                        <Box>
                                            <Typography sx={{ fontSize: '0.75rem', color: darkMode ? 'var(--lightText)' : "var(--black)", }}>{t('stopLoss')} <b style={{ fontSize: '0.7rem', }}>  {modifyPositionDetailForModify?.side === 1 ? '<' : '>'} {slValidation ? slValidation : '0.00'}</b></Typography>
                                            <Box sx={{ height: 40, display: "flex", alignItems: "center", border: `1px solid ${darkMode ? "var(--borderDark)" : 'var(--borderLight)'}`, overflow: "hidden", }} >
                                                <IconButton onClick={() => handleSLChange(false)} sx={{ height: 40, borderRight: `1px solid ${darkMode ? "var(--borderDark)" : 'var(--borderLight)'}`, borderRadius: 0, border: "none", color: darkMode ? 'var(--lightText)' : "var(--black)", fontSize: '0.9rem' }} >
                                                    <RemoveIcon fontSize="small" sx={{ fontSize: '1rem', color: darkMode ? 'var(--lightText)' : "var(--black)", }} />
                                                </IconButton>
                                                <TextField onBlur={handleSLBlur} value={stopLoss} onChange={(e) => handleChangeSL(e.target.value)} size="small" inputProps={{ style: { textAlign: "center", fontSize: '0.9rem', padding: 0 } }}
                                                    type="number" sx={{ color: darkMode ? 'var(--lightText)' : "var(--black)", textAlign: "center", "& .MuiOutlinedInput-notchedOutline": { border: "none" }, "& .MuiInputBase-root": { padding: 0 } }} />
                                                <IconButton onClick={() => handleSLChange(true)}
                                                    sx={{ height: 40, borderRight: `1px solid ${darkMode ? "var(--borderDark)" : 'var(--borderLight)'}`, borderRadius: 0, border: "none", color: darkMode ? 'var(--lightText)' : "var(--black)", }} >
                                                    <AddIcon fontSize="small" sx={{ fontSize: '1rem', p: 0 }} />
                                                </IconButton>
                                            </Box>

                                        </Box>

                                        <Box>
                                            <Typography sx={{ fontSize: '0.75rem', color: darkMode ? 'var(--lightText)' : "var(--black)", }}>{t('takeProfit')} <b style={{ fontSize: '0.7rem', }}> {modifyPositionDetailForModify?.side === 1 ? '>' : '<'} {tpValidation ? tpValidation : '0.00'}</b></Typography>
                                            <Box sx={{ height: 40, display: "flex", alignItems: "center", border: `1px solid ${darkMode ? "var(--borderDark)" : 'var(--borderLight)'}`, overflow: "hidden", }}>
                                                <IconButton onClick={() => handleChangeTP(false)} sx={{ height: 40, borderRight: `1px solid ${darkMode ? "var(--borderDark)" : 'var(--borderLight)'}`, borderRadius: 0, border: "none", color: darkMode ? 'var(--lightText)' : "var(--black)", }} >
                                                    <RemoveIcon fontSize="small" sx={{ fontSize: '1rem', }} />
                                                </IconButton>
                                                <TextField onBlur={handleTPBlur} value={takeProfit} onChange={(e) => handleTPChange(e.target.value)} size="small" inputProps={{ style: { textAlign: "center", fontSize: '0.9rem', padding: 0 } }}
                                                    type="number" sx={{ textAlign: "center", "& .MuiOutlinedInput-notchedOutline": { border: "none", }, color: darkMode ? 'var(--lightText)' : "var(--black)", "& .MuiInputBase-root": { padding: 0 } }} />
                                                <IconButton onClick={() => handleChangeTP(true)} sx={{ height: 40, borderRight: `1px solid ${darkMode ? "var(--borderDark)" : 'var(--borderLight)'}`, borderRadius: 0, border: "none", color: darkMode ? 'var(--lightText)' : "var(--black)", }}>
                                                    <AddIcon fontSize="small" sx={{ fontSize: '1rem', }} />
                                                </IconButton>
                                            </Box>

                                        </Box>
                                    </Box>
                                    <Box sx={{ display: "flex", justifyContent: 'space-evenly', alignItems: 'center', mb: 2, gap: 2, fontSize: 15, fontWeight: 500 }}>
                                        <Box>
                                            <Typography sx={{ fontSize: '0.75rem', color: darkMode ? 'var(--lightText)' : "var(--black)", textAlign: 'center' }}>{t('bid')}</Typography>
                                            <BidValue symbolId={symbolInfo?.id} digits={symbolInfo?.digits} />
                                        </Box>

                                        <Box>
                                            <Typography sx={{ fontSize: '0.75rem', color: darkMode ? 'var(--lightText)' : "var(--black)", textAlign: 'center' }}>{t('ask')}</Typography>
                                            <AskValue symbolId={symbolInfo?.id} digits={symbolInfo?.digits} />
                                        </Box>
                                    </Box>
                                    {/* <Box sx={{ mb: 2, }}><Typography sx={{ fontSize: '0.75rem', color: darkMode ? 'var(--lightText)' : "var(--black)", }}>{t('comment')}</Typography>
                                        <TextField fullWidth value={comment} size="small" inputProps={{ style: { fontSize: '0.9rem' } }} onChange={(e) => setComment(e.target.value)}
                                            sx={{ "& .MuiOutlinedInput-notchedOutline": { border: `1px solid ${darkMode ? "var(--borderDark)" : 'var(--borderLight)'}`, height: '45px', borderRadius: 0 }, color: darkMode ? 'var(--lightText)' : "var(--black)", }} />
                                    </Box> */}
                                    <Box sx={{ mb: 1 }} onClick={handleModifyClick} ><Button variant="contained" sx={{ height: 40 }} fullWidth disabled={isDisabled()}>{t('modify')}</Button></Box>
                                    <Box sx={{ backgroundColor: darkMode ? '#2e3032' : '#ec7017' }}>
                                        <Button onClick={handleClickClose} fullWidth sx={{ color: darkMode ? 'var(--lightText)' : "var(--white)", fontSize: '0.85rem', }}>{t('close')} #{modifyPositionDetailForModify?.id} {modifyPositionDetailForModify?.side === 1 ? t('buy') : t('sell')} {modifyPositionDetailForModify?.amount} <br /> {modifyPositionDetailForModify?.symbolName} </Button>
                                    </Box>
                                </>
                            )}
                        </Box>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
};

export default ModifyPosition;
